import React from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';
import { isEmpty, mapValues, isString } from 'lodash';

import useCurrentCourse from '@src/hooks/useCurrentCourse';
import {
	SOCIAL_MEDIA_FB,
	SOCIAL_MEDIA_LINE,
	SOCIAL_MEDIA_IG,
	SOCIAL_MEDIA_YOUTUBE,
	SOCIAL_MEDIA_BEHANCE,
	SOCIAL_MEDIA_PINTEREST,
	SOCIAL_MEDIA_PERSONAL_WEB,
} from '@src/const/course';

import {
	FBIcon, LineLinkIcon, IGIcon, YoutubeIcon, BehanceIcon, PinterestIcon, PersonalWebIcon,
} from '@src/components/common/icon';
// import Avatar from '@src/components/course/basicInfo/courseCard/avatar';
import { BlockWrapper } from '@src/components/common/commonStyle';

const ICON_COMPONENT_MAPPING = {
	[SOCIAL_MEDIA_FB]: FBIcon,
	[SOCIAL_MEDIA_IG]: IGIcon,
	[SOCIAL_MEDIA_LINE]: LineLinkIcon,
	[SOCIAL_MEDIA_YOUTUBE]: YoutubeIcon,
	[SOCIAL_MEDIA_BEHANCE]: BehanceIcon,
	[SOCIAL_MEDIA_PINTEREST]: PinterestIcon,
	[SOCIAL_MEDIA_PERSONAL_WEB]: PersonalWebIcon,
};

const IntroductionWrapper = styled(BlockWrapper)`
	padding-bottom: 40px;

	@media ${props => props.theme.device.smTablet} {
		padding-bottom: 32px;
	}

	@media ${props => props.theme.device.mobile} {
		padding-bottom: 32px;
	}
`;

const IntroductionContent = styled.div`
  display: flex;
	padding-top: 28px;
  & > *:not(:first-child) {
    margin-left: 24px;
  }

	@media
	${props => props.theme.device.mobile},
	${props => props.theme.device.smTablet}
	{
		& > *:not(:first-child) {
			margin-left: 12px;
		}
	}
`;

const IntroductionLeft = styled.div``;

const IntroductionRight = styled.div``;

const TeacherName = styled.div`
	font-size: 18px;
	color: #101010;
`;

const TeacherNickname = styled.div`
	font-size: 16px;
	color: #676767;
	margin: 4px 0 12px 0;
`;

const MediaIcons = styled.div`
	display: flex;
	flex-wrap: wrap;
	margin-top: 12px;
`;

const MediaIcon = styled.a`
	&:not(:last-child) {
		margin-right: 12px;
	}

	@media ${props => props.theme.device.tablet} {
		margin-right: 16px;

		&:not(:last-child) {
			margin-right: 16px;
		}
	}

	@media ${props => props.theme.device.smTablet} {
		margin-right: 16px;

		&:not(:last-child) {
			margin-right: 16px;
		}
	}

	@media ${props => props.theme.device.mobile} {
		&:not(:last-child) {
			margin-right: 16px;
		}
	}
`;

const DescriptionBlock = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 12px 8px;
	&:not(:last-child) {
		margin-top: 12px;
	}
`;

const DescriptionTitle = styled.div`
	font-weight: 500;
	color: #101010;

	@media
    ${props => props.theme.device.smTablet},
    ${props => props.theme.device.mobile}
  {
		font-size: 18px;
	}
`;

const DescriptionContent = styled.div`
	font-family: 'Roboto', 'Noto Sans TC', '微軟正黑體', sans-serif;
	white-space: pre-wrap;
	word-break: break-word;
	font-size: 16px;
	color: #9C9C9C;
	margin-bottom: 12px;
`;

const Tag = styled.div`
	display: initial;
	font-size: 14px;
	color: #9C9C9C;
	padding: 6px 12px;
	border: 1px solid #9C9C9C;
	border-radius: 24px;
	white-space: nowrap;
	:last-child {
		margin-right: unset;
	}
`;

const HeaderTitle = styled.div`
	font-weight: 700;
	font-size: 24px;
	color: #333333;
	margin-bottom: 12px;
`;

const STYLED_ICON_COMPONENT_MAPPING = mapValues(
	ICON_COMPONENT_MAPPING,
	icon => styled(icon)`
		width: 24px;
		height: 24px;
		cursor: pointer;
	`,
);

const Avatar = styled.div`
	width: 100px;
	height: 100px;
	border-radius: 50%;
	background-image: url(${props => props.image});
	background-position: center top;
	background-size: cover;

	@media
	${props => props.theme.device.mobile},
	${props => props.theme.device.smTablet}
	{
		width: 70px;
		height: 70px;
	}
`;

const TeacherIntroduction = (): JSX.Element => {
	const { t } = useTranslation();
	const { currentCourse: { teacher, extra_teachers: extraTeachers } } = useCurrentCourse();
	const teachers = [teacher, ...(extraTeachers || [])];

	return (
		<IntroductionWrapper id="lecturer_introduction">
			<HeaderTitle>{t('course:information:lecturer_introduction')}</HeaderTitle>
			{
				teachers.map(item => {
					const { cover_link, name, nickname, social_medias, about_me, skill, interest } = item;
					const skillTag = skill ? skill.split('|') : [];
					return (
						<IntroductionContent key={name}>
							<IntroductionLeft>
								<Avatar image={isString(cover_link) ? cover_link : cover_link?.link} />
							</IntroductionLeft>
							<IntroductionRight>
								<TeacherName>{name}</TeacherName>
								<TeacherNickname>{nickname}</TeacherNickname>
								{!!about_me && (
									<div className="introduction__description-block">
										<DescriptionContent>{about_me}</DescriptionContent>
									</div>
								)}
								{!!skill && (
									<DescriptionBlock>
										{
											skillTag.map(tag => <Tag key={tag}>{tag}</Tag>)
										}
									</DescriptionBlock>
								)}
								{!!interest && (
									<DescriptionBlock>
										<DescriptionTitle>{t('course:interest')}</DescriptionTitle>
										<DescriptionContent>{interest}</DescriptionContent>
									</DescriptionBlock>
								)}
								{!isEmpty(social_medias) && (
									<MediaIcons>
										{social_medias.map(media => {
											const Icon = STYLED_ICON_COMPONENT_MAPPING[media.site];
											if (!Icon) return null;
											return (
												<MediaIcon key={media.site} href={media.link} target="_blank">
													<Icon />
												</MediaIcon>
											);
										})}
									</MediaIcons>
								)}
							</IntroductionRight>
						</IntroductionContent>
					);
				})
			}
		</IntroductionWrapper>
	);
};

export default TeacherIntroduction;
